import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Clock3,
  Filter,
  ListFilter,
  Loader2,
  MessageSquareText,
  Play,
  RotateCcw,
  Save,
  Sparkles,
  UserPlus,
  UserRoundCheck,
  Users,
  X,
} from "lucide-react";
import { arenaApi } from "./data/api";
import type {
  StudentLabelStatus,
  StudentLabelSummary,
  StudentSample,
  StudentSamplesPage,
  StudentAssignment,
  StudentDirectoryUser,
  StudentEvaluationType,
  StudentTranscribability,
  StudentQueueSummary,
  StudentDraftCandidate,
} from "./data/types";
import { hasUsableProviderTranscript } from "./data/student-draft";
import { AudioPlayer } from "./AudioPlayer";

const PAGE = 20;
const REVIEW_NOTE_LIMIT = 500;
const BANDS = ["0.3-2", "2-5", "5-10", "10-20", "20-30", "30-60"];
const STATUSES: StudentLabelStatus[] = [
  "pending",
  "benchmarkable",
  "unusable",
  "needs_review",
];
type AssessmentValue =
  | "speech_full"
  | "speech_partial"
  | "no_speech"
  | "unintelligible"
  | "overlap"
  | "excluded"
  | "";

const ASSESSMENTS: Array<{ value: Exclude<AssessmentValue, "">; label: string }> = [
  { value: "speech_full", label: "Speech transcription · Full" },
  { value: "speech_partial", label: "Speech transcription · Partial" },
  { value: "no_speech", label: "No-speech detection" },
  { value: "unintelligible", label: "Exclude · Unintelligible" },
  { value: "overlap", label: "Exclude · Overlapping speakers" },
  { value: "excluded", label: "Exclude · Invalid or restricted audio" },
];

function count(summary: StudentLabelSummary | null, key: string) {
  return summary?.by_label_status[key] ?? 0;
}

function statusLabel(status: string) {
  return status.replace("_", " ");
}

function formatLabeledAt(value: string | null | undefined) {
  if (!value) return "Not labeled";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Not labeled" : date.toLocaleString();
}

function sampleDuration(sample: StudentSample) {
  return `${(sample.durationMs / 1000).toFixed(1)}s`;
}

function queueDuration(seconds: number) {
  if (seconds <= 0) return "0m";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  return `${(seconds / 60).toFixed(1)}m`;
}

const EMPTY_QUEUE_SUMMARY: StudentQueueSummary = {
  assigned: 0,
  completed: 0,
  total: 0,
  pending: 0,
  labeled: 0,
  benchmarkable: 0,
  unusable: 0,
  needs_review: 0,
  audio_seconds: 0,
  pending_audio_seconds: 0,
  estimated_work_seconds: 0,
  pending_estimated_work_seconds: 0,
  completion_percent: 0,
};

function notePreview(sample: StudentSample) {
  const note = sample.review_notes?.trim().replace(/\s+/g, " ");
  return note || "—";
}

function ProviderOutputs({ candidates, open = false }: { candidates: StudentDraftCandidate[]; open?: boolean }) {
  return (
    <details className="stt-ai-draft-candidates" open={open}>
      <summary>Provider outputs ({candidates.length})</summary>
      <div className="stt-ai-draft-candidates-list">
        {candidates.map((candidate) => (
          <div className="stt-ai-draft-candidate" key={`${candidate.provider}:${candidate.model}`}>
            <strong>{candidate.provider}:{candidate.model}</strong>
            <span>{candidate.transcript || "No transcript returned"}</span>
            {candidate.error ? <small>{candidate.error}</small> : null}
          </div>
        ))}
      </div>
    </details>
  );
}

export function StudentLabeler({
  labeledBy,
  userId,
  isManager,
}: {
  labeledBy: string;
  userId: string;
  isManager: boolean;
}) {
  const [datasetVersion, setDatasetVersion] = useState("student-v1");
  const [cohort, setCohort] = useState("");
  const [split, setSplit] = useState("");
  const [durationBand, setDurationBand] = useState("");
  const [labelStatus, setLabelStatus] = useState("");
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<StudentSamplesPage | null>(null);
  const [summary, setSummary] = useState<StudentLabelSummary | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [transcript, setTranscript] = useState("");
  const [status, setStatus] = useState<StudentLabelStatus>("pending");
  const [evaluationType, setEvaluationType] = useState<StudentEvaluationType>("speech_transcription");
  const [reason, setReason] = useState<string | null>(null);
  const [transcribability, setTranscribability] = useState<StudentTranscribability | "">("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openingPending, setOpeningPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [myQueue, setMyQueue] = useState(!isManager);
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [assignmentIdFilter, setAssignmentIdFilter] = useState("");
  const [assignmentStatusFilter, setAssignmentStatusFilter] = useState("");
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [assignmentCount, setAssignmentCount] = useState("20");
  const [members, setMembers] = useState<StudentDirectoryUser[]>([]);
  const [memberDirectory, setMemberDirectory] = useState<Record<string, StudentDirectoryUser>>({});
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [assignmentPreview, setAssignmentPreview] = useState<StudentAssignment | null>(null);
  const [assignmentCommitKey, setAssignmentCommitKey] = useState<string | null>(null);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [assignmentBusy, setAssignmentBusy] = useState(false);

  const selected = page?.items.find((sample) => sample.id === selectedId) ?? null;
  const providerTranscriptsAvailable = hasUsableProviderTranscript(selected);
  const selectedIndex = page?.items.findIndex((sample) => sample.id === selectedId) ?? -1;
  const lastPage = !page || offset + page.items.length >= page.total;
  const progress = page && page.total > 0
    ? `${offset + 1}–${Math.min(offset + page.items.length, page.total)} of ${page.total}`
    : "0 samples";
  const pageNumber = Math.floor(offset / PAGE) + 1;
  const pageCount = Math.max(1, Math.ceil((page?.total ?? 0) / PAGE));
  const totalSamples = summary?.total ?? 0;
  const totalLabeled = summary?.labeled ?? 0;
  const labeledPercent = totalSamples > 0 ? Math.round((totalLabeled / totalSamples) * 100) : 0;
  const myQueueCount = summary?.by_assignee?.[userId]?.total ?? 0;
  const assignedCount = summary?.assignment_counts?.assigned_total
    ?? summary?.assignment_counts?.assigned
    ?? ((summary?.assignment_counts?.active_assigned ?? 0) + (summary?.assignment_counts?.completed ?? 0));
  const unassignedCount = summary?.assignment_counts?.unassigned_pending
    ?? summary?.assignment_counts?.unassigned
    ?? Math.max(0, (summary?.pending ?? 0) - (summary?.assignment_counts?.active_assigned ?? summary?.assignment_counts?.assigned ?? 0));
  const allQueueCount = assignedCount;
  const queueAnalysis = summary
    ? (myQueue ? summary.by_assignee?.[userId] : summary.queue_summary) ?? EMPTY_QUEUE_SUMMARY
    : null;
  const hasActiveFilters = Boolean(cohort || split || durationBand || labelStatus || assigneeFilter || assignmentIdFilter || assignmentStatusFilter || unassignedOnly || (isManager && myQueue));
  const assessmentValue: AssessmentValue = evaluationType === "no_speech_detection"
    ? "no_speech"
    : transcribability === "full"
      ? "speech_full"
      : transcribability === "partial"
        ? "speech_partial"
        : transcribability ?? "";
  function memberLabel(memberId: string) {
    const member = memberDirectory[memberId];
    if (!member) return memberId;
    return [member.name, member.email].filter(Boolean).join(" · ") || memberId;
  }
  const versions = useMemo(() => {
    const values = summary?.available_dataset_versions ?? [];
    return Array.from(new Set(["student-v1", ...values]));
  }, [summary]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextPage, nextSummary] = await Promise.all([
        arenaApi.studentSamples({
          datasetVersion,
          cohort: cohort || undefined,
          split: split || undefined,
          durationBand: durationBand || undefined,
          labelStatus: labelStatus || undefined,
          assignedTo: myQueue ? userId : assigneeFilter || undefined,
          assignmentId: assignmentIdFilter || undefined,
          assignmentStatus: assignmentStatusFilter || undefined,
          unassigned: unassignedOnly || undefined,
          limit: PAGE,
          offset,
        }),
        arenaApi.studentSummary(datasetVersion),
      ]);
      setPage(nextPage);
      setSummary(nextSummary);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setLoading(false);
    }
  }, [assignmentIdFilter, assigneeFilter, assignmentStatusFilter, cohort, datasetVersion, durationBand, labelStatus, myQueue, offset, split, unassignedOnly, userId]);

  // Defer these state updates one tick to avoid cascading updates during the effect commit.
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (!page?.items.length || (selectedId && !page.items.some((sample) => sample.id === selectedId))) {
        setSelectedId(null);
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [page, selectedId]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (!selected) return;
      setTranscript(selected.text ?? "");
      setStatus(selected.label_status ?? "pending");
      setEvaluationType(selected.evaluation_type ?? "speech_transcription");
      setReason(selected.label_reason ?? null);
      setTranscribability(selected.transcribability ?? "");
      setReviewNotes(selected.review_notes ?? "");
    }, 0);
    return () => window.clearTimeout(timer);
  }, [selected]);

  useEffect(() => {
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setSelectedId(null);
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, []);

  function resetFilters(setter: (value: string) => void, value: string) {
    setOffset(0);
    setSelectedId(null);
    setter(value);
  }

  function clearFilters() {
    setCohort("");
    setSplit("");
    setDurationBand("");
    setLabelStatus("");
    setOffset(0);
    setSelectedId(null);
    setAssigneeFilter("");
    setAssignmentIdFilter("");
    setAssignmentStatusFilter("");
    setUnassignedOnly(false);
    setMyQueue(!isManager);
  }

  function setQueueScope(nextMyQueue: boolean) {
    setMyQueue(nextMyQueue);
    if (nextMyQueue) setUnassignedOnly(false);
    setOffset(0);
    setSelectedId(null);
  }

  function setAssignmentScope(value: string) {
    const nextUnassignedOnly = value === "unassigned";
    setUnassignedOnly(nextUnassignedOnly);
    if (nextUnassignedOnly) {
      setAssigneeFilter("");
      setAssignmentStatusFilter("");
    }
    setOffset(0);
    setSelectedId(null);
  }

  function setTeamMemberFilter(value: string) {
    setUnassignedOnly(false);
    resetFilters(setAssigneeFilter, value);
  }

  useEffect(() => {
    if (!isManager) return;
    let cancelled = false;
    void arenaApi.assignableUsers().then(async (response) => {
      if (cancelled) return;
      const rosterIds = response.users.map((member) => member.id);
      try {
        const details = await arenaApi.directoryUsers(rosterIds);
        if (cancelled) return;
        const directory = Object.fromEntries(details.users.map((member) => [member.id, member]));
        setMemberDirectory(directory);
        setMembers(rosterIds.map((id) => directory[id] ?? { id, email: "", name: null }));
      } catch (error) {
        if (cancelled) return;
        setMemberDirectory({});
        setMembers(rosterIds.map((id) => ({ id, email: "", name: null })));
        setError(String((error as Error).message ?? error));
      }
    }).catch((error) => {
      if (cancelled) return;
      setMembers([]);
      setError(String((error as Error).message ?? error));
    });
    return () => {
      cancelled = true;
    };
  }, [isManager]);

  function toggleMember(memberId: string) {
    setSelectedMembers((current) => current.includes(memberId)
      ? current.filter((id) => id !== memberId)
      : [...current, memberId]);
  }

  async function previewAssignment() {
    const requestedCount = Number(assignmentCount);
    if (!Number.isInteger(requestedCount) || requestedCount < 1 || selectedMembers.length === 0) {
      setError("Enter a positive clip count and select at least one team member.");
      return;
    }
    setAssignmentBusy(true);
    setError(null);
    try {
      const preview = await arenaApi.assignmentPreview({
        requested_count: requestedCount,
        assignee_ids: selectedMembers,
        filters: {
          dataset_version: datasetVersion,
          cohort: cohort || undefined,
          split: split || undefined,
          duration_band: durationBand || undefined,
          label_status: "pending",
        },
      });
      setAssignmentPreview(preview);
      setAssignmentCommitKey(crypto.randomUUID());
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setAssignmentBusy(false);
    }
  }

  async function commitAssignment() {
    if (!assignmentPreview) return;
    setAssignmentBusy(true);
    setError(null);
    try {
      const idempotencyKey = assignmentCommitKey ?? crypto.randomUUID();
      if (!assignmentCommitKey) setAssignmentCommitKey(idempotencyKey);
      await arenaApi.commitAssignment(assignmentPreview.assignment_id, idempotencyKey);
      setAssignmentPreview(null);
      setAssignmentCommitKey(null);
      setAssignmentOpen(false);
      await load();
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setAssignmentBusy(false);
    }
  }

  async function releaseSelectedAssignment() {
    if (!selected?.assignment_id) return;
    setAssignmentBusy(true);
    setError(null);
    try {
      await arenaApi.releaseAssignment(selected.assignment_id);
      await load();
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setAssignmentBusy(false);
    }
  }

  async function continueWithPending() {
    setOpeningPending(true);
    setError(null);
    try {
      const pendingPage = await arenaApi.studentSamples({
        datasetVersion,
        labelStatus: "pending",
        limit: PAGE,
        offset: 0,
      });
      setCohort("");
      setSplit("");
      setDurationBand("");
      setLabelStatus("pending");
      setOffset(0);
      setPage(pendingPage);
      setSelectedId(pendingPage.items[0]?.id ?? null);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setOpeningPending(false);
    }
  }

  async function save() {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      await arenaApi.labelStudentSample(selected.id, {
        label_status: status,
        evaluation_type: evaluationType,
        transcript: status === "unusable" ? null : transcript,
        reference_text: status === "unusable" ? null : transcript,
        label_reason: status === "unusable" ? reason : null,
        transcribability: transcribability || null,
        reference_scope: transcript.trim() ? "audible_words" : null,
        review_notes: reviewNotes.trim() || null,
        label_source: "human",
        labeled_by: labeledBy || "unknown",
        reviewer_id: labeledBy || "unknown",
        adjudication_status: status === "benchmarkable" || status === "unusable" ? "approved" : "pending",
      });
      await load();
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setSaving(false);
    }
  }

  async function generateDraft() {
    if (!selected) return;
    setDrafting(true);
    setError(null);
    try {
      await arenaApi.suggestStudentLabel(selected.id, true);
      await load();
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setDrafting(false);
    }
  }

  function acceptDraft() {
    if (!selected?.draft_transcript?.trim()) return;
    setTranscript(selected.draft_transcript);
    setStatus(selected.draft_label_status === "benchmarkable" ? "benchmarkable" : "needs_review");
    setTranscribability(selected.draft_transcribability ?? "partial");
  }

  function changeAssessment(value: AssessmentValue) {
    if (value === "no_speech") {
      setTranscript("");
      setEvaluationType("no_speech_detection");
      setTranscribability("no_speech");
      setStatus("benchmarkable");
      setReason(null);
      return;
    }

    if (!value) {
      setEvaluationType("speech_transcription");
      setTranscribability("");
      setStatus("pending");
      setReason(null);
      return;
    }

    setEvaluationType("speech_transcription");
    const nextTranscribability: StudentTranscribability = value === "speech_full"
      ? "full"
      : value === "speech_partial"
        ? "partial"
        : value;
    setTranscribability(nextTranscribability);

    if (value === "unintelligible" || value === "overlap" || value === "excluded") {
      setStatus("unusable");
      setReason(value === "excluded" ? "other" : value);
    } else if (evaluationType === "no_speech_detection") {
      setEvaluationType("speech_transcription");
      setStatus("pending");
      setReason(null);
    }
  }

  function move(delta: number) {
    if (!page || selectedIndex < 0) return;
    const nextIndex = selectedIndex + delta;
    if (nextIndex >= 0 && nextIndex < page.items.length) {
      setSelectedId(page.items[nextIndex].id);
      return;
    }
    if (delta > 0 && !lastPage) {
      setSelectedId(null);
      setOffset(offset + PAGE);
    } else if (delta < 0 && offset > 0) {
      setSelectedId(null);
      setOffset(Math.max(0, offset - PAGE));
    }
  }

  return (
    <div className="stt-student-labeler">
      <header className="stt-student-page-header">
        <div className="stt-student-page-copy">
          <div className="stt-page-eyebrow"><ClipboardCheck size={14} /> Student audio dataset</div>
          <h2>Label students</h2>
          <p>Review audio, confirm the assessment, and save human-approved labels for benchmarking.</p>
        </div>
        <div className="stt-student-page-actions">
          <label className="stt-filter-control" htmlFor="student-dataset">
            <span className="stt-field-label">Dataset</span>
            <select id="student-dataset" className="stt-select" value={datasetVersion} onChange={(e) => resetFilters(setDatasetVersion, e.target.value)}>
              {versions.map((version) => <option key={version} value={version}>{version}</option>)}
            </select>
          </label>
          <button className="stt-btn stt-btn--primary stt-continue-btn" type="button" onClick={() => void continueWithPending()} disabled={loading || openingPending || !summary?.pending}>
            {openingPending ? <Loader2 className="stt-spin" size={15} /> : <ArrowRight size={15} />} {openingPending ? "Opening queue" : "Continue labeling"}
          </button>
        </div>
      </header>

      <div className="stt-label-progress" aria-label={`${labeledPercent}% of samples labeled`}>
        <div className="stt-label-progress-head">
          <div>
            <strong>{totalLabeled} of {totalSamples}</strong>
            <span>samples labeled</span>
          </div>
          <span className="stt-label-progress-percent">{labeledPercent}%</span>
        </div>
        <div className="stt-label-progress-track" aria-hidden="true">
          <span style={{ width: `${labeledPercent}%` }} />
        </div>
      </div>

      <div className="stt-label-summary" aria-label="Labeling summary">
        <div className="stt-label-summary-item stt-label-summary-item--total"><strong>{summary?.total ?? "—"}</strong><span>Total samples</span></div>
        <div className="stt-label-summary-item stt-label-summary-item--labeled"><strong>{summary?.labeled ?? "—"}</strong><span>Total labeled</span></div>
        <div className="stt-label-summary-item stt-label-summary-item--assigned"><strong>{summary ? assignedCount : "—"}</strong><span>Assigned</span></div>
        <div className="stt-label-summary-item stt-label-summary-item--unassigned" title="Pending samples without an active assignment"><strong>{summary ? unassignedCount : "—"}</strong><span>Unassigned to label</span></div>
        <div className="stt-label-summary-item stt-label-summary-item--success"><strong>{summary?.benchmarkable ?? "—"}</strong><span>Benchmarkable</span></div>
        <div className="stt-label-summary-item stt-label-summary-item--pending"><strong>{summary?.pending ?? count(summary, "pending")}</strong><span>Remaining</span></div>
        <div className="stt-label-summary-item stt-label-summary-item--danger"><strong>{count(summary, "unusable")}</strong><span>Unusable</span></div>
        <div className="stt-label-summary-item stt-label-summary-item--review"><strong>{count(summary, "needs_review")}</strong><span>Needs review</span></div>
      </div>

      {isManager ? (
        <section className="stt-assignment-panel" aria-labelledby="stt-assignment-title">
          <button className="stt-assignment-toggle" type="button" onClick={() => setAssignmentOpen((open) => !open)} aria-expanded={assignmentOpen}>
            <span><UserPlus size={15} /> Assign labeling work</span>
            <span>{assignmentOpen ? "Hide" : "Open"}</span>
          </button>
          {assignmentOpen ? (
            <div className="stt-assignment-body">
              <div className="stt-assignment-fields">
                <label className="stt-filter-control">
                  <span className="stt-field-label">Total clips</span>
                  <input className="stt-input" type="number" min="1" value={assignmentCount} onChange={(event) => setAssignmentCount(event.target.value)} />
                </label>
                <button className="stt-btn stt-btn--soft" type="button" onClick={() => void previewAssignment()} disabled={assignmentBusy || selectedMembers.length === 0}>
                  {assignmentBusy ? <Loader2 className="stt-spin" size={14} /> : <Clock3 size={14} />} Preview split
                </button>
              </div>
              {members.length ? (
                <div className="stt-member-results" role="listbox" aria-label="Team members">
                  {members.map((member) => (
                    <button key={member.id} className={`stt-member-option${selectedMembers.includes(member.id) ? " stt-member-option--selected" : ""}`} type="button" onClick={() => toggleMember(member.id)}>
                      <Users size={13} />
                      <span className="stt-member-option-label">
                        <strong>{member.name || `User ${member.id}`}</strong>
                        <small>{member.email || `ID ${member.id}`}</small>
                      </span>
                    </button>
                  ))}
                </div>
              ) : <p className="stt-assignment-empty">No assignable users are configured.</p>}
              {selectedMembers.length ? <div className="stt-selected-members">{selectedMembers.length} team member{selectedMembers.length === 1 ? "" : "s"} selected</div> : null}
              {assignmentPreview ? (
                <div className="stt-assignment-preview">
                  <div className="stt-assignment-preview-head"><strong>Allocation preview</strong><span>{assignmentPreview.selected_count} selected{assignmentPreview.shortfall ? ` · ${assignmentPreview.shortfall} short` : ""}</span></div>
                  <div className="stt-assignment-allocation-grid">
                    {assignmentPreview.allocations.map((allocation) => (
                      <div key={allocation.assignee_id} className="stt-assignment-allocation">
                        <strong>{memberLabel(allocation.assignee_id)}</strong>
                        <span>{allocation.sample_count} clips · {(allocation.estimated_work_seconds / 60).toFixed(1)} min</span>
                        <small>{allocation.difference_from_average_seconds >= 0 ? "+" : ""}{(allocation.difference_from_average_seconds / 60).toFixed(1)} min from average</small>
                      </div>
                    ))}
                  </div>
                  <button className="stt-btn stt-btn--primary" type="button" onClick={() => void commitAssignment()} disabled={assignmentBusy || assignmentPreview.selected_count === 0}>
                    {assignmentBusy ? <Loader2 className="stt-spin" size={14} /> : <Check size={14} />} Confirm assignment
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}

      <div className="stt-student-filter-bar">
        <div className="stt-student-filter-title"><Filter size={14} /><span>Filter samples</span></div>
        <label className="stt-filter-control">
          <span className="stt-field-label">Cohort</span>
          <select className="stt-select" value={cohort} onChange={(e) => resetFilters(setCohort, e.target.value)}>
            <option value="">All cohorts</option>
            <option value="prevalence">Prevalence</option>
            <option value="eval">Evaluation</option>
          </select>
        </label>
        <div className="stt-queue-scope" aria-label="Queue scope">
          <span className="stt-field-label">Queue</span>
          <div className="stt-queue-segmented" role="group" aria-label="Choose queue scope">
            <button
              className={`stt-queue-option${myQueue ? " stt-queue-option--active" : ""}`}
              type="button"
              aria-pressed={myQueue}
              onClick={() => setQueueScope(true)}
            >
              <UserRoundCheck size={13} /> My queue <span className="stt-queue-count">{myQueueCount}</span>
            </button>
            <button
              className={`stt-queue-option${!myQueue ? " stt-queue-option--active" : ""}`}
              type="button"
              aria-pressed={!myQueue}
              disabled={!isManager}
              title={!isManager ? "Only managers can view all queues" : undefined}
              onClick={() => setQueueScope(false)}
            >
              <ListFilter size={13} /> All queues <span className="stt-queue-count">{allQueueCount}</span>
            </button>
          </div>
          <span className="stt-queue-viewing" role="status">Viewing {myQueue ? "My queue" : "All queues"}</span>
        </div>
        <label className="stt-filter-control">
          <span className="stt-field-label">Assignment status</span>
          <select className="stt-select" value={assignmentStatusFilter} onChange={(e) => resetFilters(setAssignmentStatusFilter, e.target.value)}>
            <option value="">All statuses</option>
            <option value="assigned">Assigned</option>
            <option value="completed">Completed</option>
          </select>
        </label>
        <label className="stt-filter-control">
          <span className="stt-field-label">Assignment scope</span>
          <select className="stt-select" value={unassignedOnly ? "unassigned" : ""} disabled={myQueue} onChange={(e) => setAssignmentScope(e.target.value)} title={myQueue ? "Switch to All queues to view unassigned samples" : undefined}>
            <option value="">All samples</option>
            <option value="unassigned">Unassigned only</option>
          </select>
        </label>
        <label className="stt-filter-control">
          <span className="stt-field-label">Team member</span>
          <select className="stt-select stt-assignee-filter" value={assigneeFilter} disabled={myQueue} onChange={(e) => setTeamMemberFilter(e.target.value)} title={myQueue ? "Switch to All queues to filter by team member" : undefined}>
            <option value="">All team members</option>
            {members.map((member) => <option key={member.id} value={member.id}>{memberLabel(member.id)}</option>)}
          </select>
        </label>
        <label className="stt-filter-control">
          <span className="stt-field-label">Batch</span>
          <input className="stt-input stt-assignee-filter" value={assignmentIdFilter} onChange={(e) => resetFilters(setAssignmentIdFilter, e.target.value)} placeholder="Assignment ID" />
        </label>
        <label className="stt-filter-control">
          <span className="stt-field-label">Split</span>
          <select className="stt-select" value={split} onChange={(e) => resetFilters(setSplit, e.target.value)}>
            <option value="">All splits</option>
            <option value="development">Development</option>
            <option value="validation">Validation</option>
            <option value="locked_holdout">Locked holdout</option>
          </select>
        </label>
        <label className="stt-filter-control">
          <span className="stt-field-label">Duration</span>
          <select className="stt-select" value={durationBand} onChange={(e) => resetFilters(setDurationBand, e.target.value)}>
            <option value="">All durations</option>
            {BANDS.map((band) => <option key={band} value={band}>{band}s</option>)}
          </select>
        </label>
        <label className="stt-filter-control">
          <span className="stt-field-label">Label status</span>
          <select className="stt-select" value={labelStatus} onChange={(e) => resetFilters(setLabelStatus, e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((item) => <option key={item} value={item}>{statusLabel(item)}</option>)}
          </select>
        </label>
        {hasActiveFilters ? (
          <button className="stt-filter-reset" type="button" onClick={clearFilters}>
            <RotateCcw size={13} /> Clear filters
          </button>
        ) : null}
        <span className="stt-result-count">{progress}</span>
      </div>

      {queueAnalysis ? (
        <section className="stt-queue-analysis" aria-labelledby="stt-queue-analysis-title">
          <div className="stt-queue-analysis-head">
            <div>
              <div id="stt-queue-analysis-title" className="stt-panel-title">
                {myQueue ? "My queue analysis" : "All queues analysis"}
              </div>
              <p>
                {queueAnalysis.pending} sample{queueAnalysis.pending === 1 ? "" : "s"} left for labeling
                {queueAnalysis.pending_estimated_work_seconds > 0
                  ? ` · about ${queueDuration(queueAnalysis.pending_estimated_work_seconds)} of review work`
                  : ""}
              </p>
            </div>
            <div className="stt-queue-analysis-completion">
              <strong>{queueAnalysis.completion_percent}%</strong>
              <span>complete</span>
            </div>
          </div>
          <div className="stt-queue-analysis-grid">
            <div className="stt-queue-analysis-metric stt-queue-analysis-metric--pending">
              <strong>{queueAnalysis.pending}</strong>
              <span>Left to label</span>
            </div>
            <div className="stt-queue-analysis-metric">
              <strong>{queueAnalysis.completed}</strong>
              <span>Completed assignments</span>
            </div>
            <div className="stt-queue-analysis-metric stt-queue-analysis-metric--success">
              <strong>{queueAnalysis.benchmarkable}</strong>
              <span>Benchmarkable</span>
            </div>
            <div className="stt-queue-analysis-metric stt-queue-analysis-metric--review">
              <strong>{queueAnalysis.needs_review + queueAnalysis.unusable}</strong>
              <span>Needs review / unusable</span>
            </div>
          </div>
          <div className="stt-queue-analysis-workload">
            <div className="stt-queue-analysis-workload-head">
              <span>Labeling progress</span>
              <strong>{queueAnalysis.labeled} of {queueAnalysis.total}</strong>
            </div>
            <div className="stt-queue-analysis-track" aria-hidden="true">
              <span style={{ width: `${queueAnalysis.completion_percent}%` }} />
            </div>
            <div className="stt-queue-analysis-details">
              <span>Audio: {queueDuration(queueAnalysis.audio_seconds)}</span>
              <span>Estimated total: {queueDuration(queueAnalysis.estimated_work_seconds)}</span>
              <span>Unusable: {queueAnalysis.unusable}</span>
            </div>
          </div>
        </section>
      ) : null}

      {error ? <div className="stt-empty stt-empty--err">{error}</div> : null}

      <div className={`stt-student-workspace${selected ? " stt-student-workspace--selected" : ""}`}>
        <div className="stt-student-list" aria-label="Student audio samples">
          {loading && !page ? (
            <div className="stt-loading"><Loader2 className="stt-spin" size={18} /> Loading student audio…</div>
          ) : !page?.items.length ? (
            <div className="stt-empty">No samples match these filters.</div>
          ) : (
            <div className="stt-table-wrap stt-student-table-wrap">
              <table className="stt-table stt-student-table">
                <thead>
                  <tr>
                    <th>Sample</th>
                    <th>Audio</th>
                    <th>Category</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th>Assignment</th>
                    <th>Transcript</th>
                    <th>Review notes</th>
                    <th>Labeled by</th>
                  </tr>
                </thead>
                <tbody>
                  {page.items.map((sample) => {
                    const isSelected = sample.id === selectedId;
                    return (
                      <tr
                        key={sample.id}
                        className={isSelected ? "stt-student-row stt-student-row--selected" : "stt-student-row"}
                        aria-selected={isSelected}
                        onClick={() => setSelectedId(sample.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelectedId(sample.id);
                          }
                        }}
                        tabIndex={0}
                      >
                        <td><code className="stt-student-id">{sample.id}</code></td>
                        <td>
                          <button
                            className="stt-row-play"
                            type="button"
                            title="Open audio editor"
                            aria-label={`Open audio editor for ${sample.id}`}
                            onClick={(e) => { e.stopPropagation(); setSelectedId(sample.id); }}
                          >
                            <Play size={13} />
                          </button>
                        </td>
                        <td>
                          <div className="stt-student-category">
                            <span className="stt-badge">{sample.cohort}</span>
                            <span className="stt-badge stt-badge--ghost">{statusLabel(sample.split)}</span>
                            <span className="stt-badge stt-badge--ghost">{sample.duration_band}s</span>
                          </div>
                        </td>
                        <td className="num">{sampleDuration(sample)}</td>
                        <td><span className={`stt-status-badge stt-status-badge--${sample.label_status}`}>{statusLabel(sample.label_status)}</span></td>
                        <td className="stt-assignment-cell"><span>{sample.assignee_id ? memberLabel(sample.assignee_id) : "Unassigned"}</span><small>{sample.assignment_status || "—"}</small></td>
                        <td className="stt-transcript-preview">{sample.text?.trim() || "—"}</td>
                        <td className="stt-review-notes-preview">
                          <span
                            role="note"
                            title={sample.review_notes?.trim() || undefined}
                            aria-label={sample.review_notes?.trim() ? `Review notes: ${sample.review_notes.trim()}` : "No review notes"}
                          >
                            {notePreview(sample)}
                          </span>
                        </td>
                        <td className="stt-labeled-meta">
                          <span>{sample.labeled_by || "—"}</span>
                          <small>{formatLabeledAt(sample.labeled_at)}</small>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="stt-pager stt-student-pager">
            <span>Page {pageNumber} of {pageCount}</span>
            <button type="button" disabled={offset === 0 || loading} onClick={() => { setSelectedId(null); setOffset(Math.max(0, offset - PAGE)); }}>
              <ChevronLeft size={14} /> Previous
            </button>
            <button type="button" disabled={lastPage || loading} onClick={() => { setSelectedId(null); setOffset(offset + PAGE); }}>
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>

        {selected ? (
          <>
            <button className="stt-student-editor-backdrop" type="button" aria-label="Close label editor" onClick={() => setSelectedId(null)} />
            <aside id="student-label-editor" aria-label="Student label editor" className="stt-label-panel stt-student-editor stt-student-editor--open">
            <div className="stt-label-head">
              <div>
                <div id="student-label-editor-title" className="stt-student-editor-title">Label sample</div>
                <div className="stt-sample-badges">
                  <span className="stt-badge">{selected.cohort}</span>
                  <span className="stt-badge stt-badge--ghost">{statusLabel(selected.split)}</span>
                  <span className="stt-badge stt-badge--ghost">{selected.duration_band}s</span>
                  {selected.split === "locked_holdout" ? <span className="stt-badge stt-badge--holdout">locked</span> : null}
                </div>
                <div className="stt-label-id">{selected.id} · {sampleDuration(selected)}</div>
              </div>
              <button className="stt-icon-btn" type="button" title="Close editor" aria-label="Close editor" onClick={() => setSelectedId(null)}>
                <X size={15} />
              </button>
              {isManager && selected.assignment_status === "assigned" ? (
                <button className="stt-icon-btn" type="button" title="Release assignment" aria-label="Release assignment" disabled={assignmentBusy} onClick={() => void releaseSelectedAssignment()}>
                  {assignmentBusy ? <Loader2 className="stt-spin" size={15} /> : <RotateCcw size={15} />}
                </button>
              ) : null}
            </div>

            <div className="stt-student-editor-body">
            <AudioPlayer uri={selected.cleanUri} tag="Student audio" />
            <div className="stt-ai-draft-panel">
              <div className="stt-ai-draft-head">
                <div>
                  <div className="stt-field-label"><Sparkles size={13} /> AI draft</div>
                  <small>Draft only. Saving the label remains a human approval.</small>
                </div>
                <button className="stt-btn stt-btn--soft" type="button" disabled={drafting} onClick={() => void generateDraft()}>
                  {drafting ? <Loader2 className="stt-spin" size={14} /> : <Sparkles size={14} />}
                  {drafting ? "Generating" : selected.draft_created_at ? "Regenerate" : "Generate draft"}
                </button>
              </div>
              {selected.draft_transcript?.trim() ? (
                <>
                  <div className="stt-ai-draft-text">{selected.draft_transcript}</div>
                  <div className="stt-ai-draft-meta">
                    <span>Agreement confidence: {Math.round((selected.draft_confidence ?? 0) * 100)}%</span>
                    <span>{selected.draft_agreeing_provider_count ?? 0}/{selected.draft_valid_provider_count ?? 0} usable transcripts agree</span>
                    <span>{selected.draft_valid_provider_count ?? 0}/{selected.draft_provider_count ?? 0} providers returned text</span>
                    {selected.draft_agreement_similarity != null ? (
                      selected.draft_agreeing_provider_count && selected.draft_agreeing_provider_count > 1 ? (
                        <span>Peer similarity: {Math.round(selected.draft_agreement_similarity * 100)}%</span>
                      ) : (
                        <span>No cross-provider match</span>
                      )
                    ) : (
                      <span>Legacy draft; regenerate to recalculate agreement</span>
                    )}
                  </div>
                  <ProviderOutputs candidates={selected.draft_candidates ?? []} />
                  <button className="stt-btn stt-btn--soft" type="button" onClick={acceptDraft}>
                    <Check size={14} /> Use draft in label
                  </button>
                </>
              ) : providerTranscriptsAvailable ? (
                <div className="stt-ai-draft-review">
                  <strong>Providers returned transcripts, but no consensus draft is available.</strong>
                  <span>Review provider outputs and enter a human-approved transcript.</span>
                  <ProviderOutputs candidates={selected.draft_candidates ?? []} open />
                </div>
              ) : selected.draft_created_at ? (
                <div className="stt-ai-draft-failure">
                  <strong>No provider returned a transcript.</strong>
                  <span>Check the provider credentials, then regenerate.</span>
                  {selected.draft_candidates?.filter((candidate) => candidate.error).map((candidate) => (
                    <small key={`${candidate.provider}:${candidate.model}`}>
                      {candidate.provider}:{candidate.model} — {candidate.error}
                    </small>
                  ))}
                </div>
              ) : drafting ? <div className="stt-ai-draft-empty">Running the configured STT providers…</div> : (
                <div className="stt-ai-draft-empty">Generate a provider ensemble draft to start.</div>
              )}
            </div>
            <label className="stt-field-label" htmlFor="student-transcript">Human transcript</label>
            <textarea
              id="student-transcript"
              className="stt-textarea stt-label-transcript"
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              disabled={status === "unusable" || evaluationType === "no_speech_detection"}
              placeholder={evaluationType === "no_speech_detection" ? "No transcript required for confirmed silence" : "Type exactly what is intelligible in the clip"}
              rows={5}
            />

            <div className="stt-label-controls">
              <div className="stt-label-control">
                <label className="stt-field-label" htmlFor="student-assessment">Assessment</label>
                <select
                  id="student-assessment"
                  className="stt-select"
                  value={assessmentValue}
                  onChange={(e) => changeAssessment(e.target.value as AssessmentValue)}
                >
                  <option value="">Choose assessment</option>
                  {ASSESSMENTS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </div>
              <div className="stt-label-control">
                <label className="stt-field-label" htmlFor="student-status">Decision</label>
                <select id="student-status" className="stt-select" value={status} onChange={(e) => setStatus(e.target.value as StudentLabelStatus)}>
                  {STATUSES.map((item) => (
                    <option
                      key={item}
                      value={item}
                      disabled={item === "benchmarkable" && evaluationType === "speech_transcription" && ["no_speech", "unintelligible", "overlap", "excluded"].includes(transcribability)}
                    >
                      {statusLabel(item)}
                    </option>
                  ))}
                </select>
              </div>
              {status === "unusable" ? (
                <div className="stt-label-control">
                  <label className="stt-field-label" htmlFor="student-label-reason">Reason</label>
                  <select id="student-label-reason" className="stt-select" value={reason ?? ""} onChange={(e) => setReason(e.target.value || null)}>
                    <option value="">Choose reason</option>
                    <option value="non_speech">Non-speech</option>
                    <option value="unintelligible">Unintelligible</option>
                    <option value="overlap">Overlapping speakers</option>
                    <option value="clipped">Clipped audio</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              ) : null}
              {evaluationType === "no_speech_detection" ? <div className="stt-label-note stt-label-note--info"><Check size={14} /> Empty provider output is a correct no-speech result.</div> : null}
            </div>

            <section className="stt-review-notes" aria-labelledby="student-review-notes-label">
              <div className="stt-review-notes-head">
                <label id="student-review-notes-label" className="stt-field-label" htmlFor="student-review-notes">
                  <MessageSquareText size={14} /> Review notes
                </label>
                <span id="student-review-notes-count" className="stt-review-notes-count" role="status">
                  {reviewNotes.length}/{REVIEW_NOTE_LIMIT}
                </span>
              </div>
              <textarea
                id="student-review-notes"
                className="stt-textarea stt-review-notes-input"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add context for the next reviewer"
                maxLength={REVIEW_NOTE_LIMIT}
                aria-describedby="student-review-notes-count"
                rows={4}
              />
            </section>
            </div>

            <div className="stt-label-footer">
              <div className="stt-label-actions">
                <button className="stt-btn stt-btn--soft" disabled={saving || selectedIndex <= 0 && offset === 0} onClick={() => move(-1)}>
                  <ChevronLeft size={15} /> Previous
                </button>
                <button className="stt-btn stt-btn--primary" disabled={saving || (status !== "pending" && !transcribability) || (evaluationType === "speech_transcription" && status === "benchmarkable" && !transcript.trim()) || (evaluationType === "no_speech_detection" && (status !== "benchmarkable" || transcribability !== "no_speech" || transcript.trim())) || (status === "unusable" && !reason)} onClick={() => void save()}>
                  {saving ? <Loader2 className="stt-spin" size={15} /> : <Save size={15} />} Save label
                </button>
                <button className="stt-btn stt-btn--soft" disabled={saving || (selectedIndex < 0 || lastPage && selectedIndex === (page?.items.length ?? 0) - 1)} onClick={() => move(1)}>
                  Next <ChevronRight size={15} />
                </button>
              </div>
              {selected.label_status === "benchmarkable" ? <div className="stt-label-note"><Check size={14} /> This clip is eligible for provider benchmarking.</div> : null}
            </div>
            </aside>
          </>
        ) : (
          <aside className="stt-label-panel stt-student-editor stt-student-editor--empty">
            <div className="stt-empty-editor-icon"><ClipboardCheck size={20} /></div>
            <strong>Select a sample</strong>
            <span>Choose a row to review its audio and label.</span>
          </aside>
        )}
      </div>

      <section className="stt-student-matrix">
        <div className="stt-coverage-head">
          <div>
            <div className="stt-panel-title">Coverage by sampling category</div>
            <p>Track labeling progress across cohorts, splits, and duration bands.</p>
          </div>
          <CheckCircle2 size={18} aria-hidden="true" />
        </div>
        {summary?.status_matrix.length ? (
          <div className="stt-table-wrap">
            <table className="stt-table stt-label-matrix">
              <thead>
                <tr><th>Category</th><th className="num">Total</th><th className="num">Labeled</th>{STATUSES.map((item) => <th key={item} className="num">{statusLabel(item)}</th>)}</tr>
              </thead>
              <tbody>
                {summary.status_matrix.map((item) => (
                  <tr key={`${item.cohort}-${item.split}-${item.duration_band}`}>
                    <td>{item.cohort} / {statusLabel(item.split)} / {item.duration_band}s</td>
                    <td className="num">{item.total}</td>
                    <td className="num">{item.labeled}</td>
                    {STATUSES.map((statusItem) => <td key={statusItem} className="num">{item[statusItem]}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="stt-empty">No category totals available.</div>}
      </section>
    </div>
  );
}
